import { CheckBox, InputField } from '@repo/ui/components';
import { ICalculation, IFinancing } from 'next-common';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { FormRow } from '../../FormRow';
import { fieldIds } from './FinancingForm';
import { get } from 'lodash';
import { getNominalInterest, getRequestedNominalInterest } from './FinancingForm.utils';
import { cn, formatNumber, InputType } from 'types';

export interface IInterestFormRowProps {
  type?: number;
  calculation: ICalculation;
  isEditable?: boolean;
  isLoading?: boolean;
  isUsedCar?: boolean;
  onInterestChange(e: SyntheticEvent, field: string, originalValue: any);
  isRenteVisible?: boolean;
  setIsRenteVisible?(isRenteVisible: boolean): void;
  isForeignBrand?: boolean;
}

const getInterestValue = (financing: IFinancing) => {
  return getRequestedNominalInterest(financing) || getNominalInterest(financing) || 0;
};

export const InterestFormRow = (props: IInterestFormRowProps) => {
  const [interest, setInterest] = useState(getInterestValue(props.calculation?.financing).toString());
  const [interestLocked, setInterestLocked] = useState<boolean>(true);
  const hasComponentMounted = useRef<boolean>(false);
  useEffect(() => {
    if (!props.calculation) return;
    const current = formatNumber(getInterestValue(props.calculation?.financing), 2);
    setInterest(current);
  }, []);

  useEffect(() => {
    const newInterest = formatNumber(getInterestValue(props.calculation?.financing), 2);
    setInterest(newInterest);
  }, [props.calculation]);

  useEffect(() => {
    if (hasComponentMounted.current && !props.isRenteVisible && interestLocked) {
      const current = getInterestValue(props.calculation?.financing);
      setInterest(`${current}`);
      const e = { currentTarget: { value: current.toString() } };
      onInputChange(e);
    } else {
      hasComponentMounted.current = true;
    }
  }, [props.isRenteVisible, interestLocked]);

  const onInputChange = (e) => {
    let valueString: string = e?.currentTarget?.value ?? e.target.value;
    valueString = valueString?.replace(',', '.');
    const value: number = parseFloat(valueString);
    setInterest(formatNumber(value, 2));
    props.onInterestChange(e, fieldIds.manualNominalInterest, interest);
  };

  const handleClick = () => {
    props.setIsRenteVisible(!props.isRenteVisible);
    setInterestLocked(!interestLocked);
  };

  if (!props.isEditable) {
    return (
      <FormRow
        id={fieldIds.manualNominalInterest}
        isValid
        isEditable={false}
        label={
          <div className="flex gap-0.5">
            Rente
            <div className="flex gap-[3px] items-center">
              {' ('}
              <button
                className={cn(
                  'text-base/[175%] font-semibold border-b-2',
                  interestLocked ? 'cursor-pointer border-turquoise' : 'border-transparent'
                )}
                {...(interestLocked && { onClick: () => handleClick() })}
              >
                overstyrt
              </button>
              {','}
              <button
                className={cn(
                  'text-base/[175%] font-semibold border-b-2',
                  !interestLocked ? 'cursor-pointer border-turquoise' : 'border-transparent'
                )}
                {...(!interestLocked && { onClick: () => handleClick() })}
              >
                tilbakestill rente
              </button>
              {' )'}
            </div>
          </div>
        }
        value={interest}
        valuePostfix={' %'}
        type={InputType.FLOAT}
        isUsedCar={props.isUsedCar}
      />
    );
  }

  return (
    <>
      {props?.isUsedCar || props?.isForeignBrand ? (
        <>
          <div className="flex flex-col gap-1 flex-wrap">
            <label
              className={cn(
                'text-base/[175%] font-semibold',
                props.isLoading || props.isRenteVisible ? 'text-neutralGrey' : ''
              )}
              htmlFor={fieldIds.manualNominalInterest}
            >
              Overstyr rente
            </label>
            <InputField
              decimals={2}
              disabled={props.isLoading || props.isRenteVisible}
              id={fieldIds.manualNominalInterest}
              type={InputType.FLOAT}
              endAdornment={{ content: '%' }}
              key={`${fieldIds.manualNominalInterest}_${get(props, 'calculation.id')}`}
              value={interest}
              onChange={(input: string, formatted?: string, e?: React.ChangeEvent<any>) => onInputChange(e)}
              onKeyDown={(e: React.KeyboardEvent<any>) => {
                if (e.key === 'Enter') {
                  onInputChange(e);
                }
              }}
            />
          </div>
          <CheckBox
            label={'Overstyr rente'}
            id={'includeMva'}
            onChange={(value: React.ChangeEvent<HTMLInputElement>) => props.setIsRenteVisible(value?.target?.checked)}
            checked={!props?.isRenteVisible}
          />
        </>
      ) : (
        props.isRenteVisible && (
          <div className="flex flex-col flex-wrap">
            <label
              className={cn('text-base/[175%] font-semibold', props?.isLoading ? 'text-lightGrey' : '')}
              htmlFor={fieldIds.manualNominalInterest}
            >
              Overstyr rente
            </label>
            <InputField
              decimals={2}
              disabled={props.isLoading}
              id={fieldIds.manualNominalInterest}
              type={InputType.FLOAT}
              endAdornment={{ content: '%' }}
              key={`${fieldIds.manualNominalInterest}_${get(props, 'calculation.id')}`}
              value={interest}
              onChange={(input: string, formatted?: string, e?: React.ChangeEvent<any>) => onInputChange(e)}
              onKeyDown={(e: React.KeyboardEvent<any>) => {
                if (e.key === 'Enter') {
                  onInputChange(e);
                }
              }}
            />
            <FormRow
              id={fieldIds.manualNominalInterest}
              isValid
              isEditable={false}
              label={`Standard Rente: ${getNominalInterest(props.calculation?.financing)?.toString()} %`}
              valuePostfix={' '}
              type={InputType.FLOAT}
              collapsedWarning
              decimals={2}
              className="text-right gap-2"
            />
          </div>
        )
      )}
    </>
  );
};
